import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, Link, Mail, Target, User } from 'lucide-react';

export const coldOutreach: Playbook = {
  meta: {
    id: 'cold-outreach',
    slug: 'cold-outreach',
    categoryId: 'capture-leads',
    name: 'The Sales Prospecting Link',
    tagline: 'Send voice Genie links for sales outreach.',
    description: 'Send personalized voice Genie links to prospects via email or LinkedIn. When they click, your Genie qualifies interest and books meetings.',
    strategy: 'Send personalized voice Genie links to prospects via email or LinkedIn. When they click, your Genie qualifies their interest and books meetings.',
    benefits: [
      'Higher response rates than emails',
      'Automated qualification process',
      'Personalized at scale',
      'Direct meeting booking'
    ],
    targetAudience: ['B2B sales teams', 'Business development', 'Lead generation agencies'],
    estimatedSetupTime: '18 minutes',
    difficulty: 'intermediate',
    tags: ['cold-outreach', 'prospecting', 'linkedin', 'email-outreach'],
    isFeatured: false
  },
  seo: {
    metaTitle: 'Cold Outreach Playbook - Genie Sales Prospecting Links',
    metaDescription: 'Transform cold outreach with personalized Genie links. Higher response rates, automated qualification, and direct booking.',
    keywords: ['cold outreach', 'sales prospecting', 'linkedin outreach', 'email prospecting', 'Genie sales']
  },
  demoConfig: {
    triggerType: 'immediate',
    message: "Hi! Thanks for clicking through. I'm Sarah from TechCorp. John suggested we connect because we help companies like yours reduce support costs by 60%. I'd love to learn about your current setup - do you have a quick moment?",
    position: 'center'
  },
  demoScript: [
    { speaker: 'agent', message: "Hi! Thanks for clicking through. We help companies reduce support costs. Can I ask you a quick question?" },
    { speaker: 'user', message: "Sure, what do you do exactly?" },
    { speaker: 'agent', message: "We provide voice Genies that handle customer inquiries 24/7. Most clients see 60% cost reduction." },
    { speaker: 'user', message: "That sounds interesting" },
    { speaker: 'agent', message: "Great! Based on what you've told me, I think you'd love our solution. Can I schedule a 15-minute demo for you?" }
  ],
  templateConfig: {
    firstMessage: "Hi! Thanks for clicking through. I'm [NAME] from [COMPANY]. [REFERRER] thought we should connect because [VALUE_PROP].",
    systemPrompt: "You are a friendly sales Genie accessed via a shared link. The prospect clicked your link from an email or message. Be warm, quickly communicate value, qualify their interest, and book meetings.",
    tools: ['calendar_booking', 'lead_scoring', 'email_capture', 'crm_integration']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'Prospect clicks outreach link',
      description: 'Target receives personalized email/LinkedIn message with Genie link and clicks'
    },
    {
      type: 'genie',
      icon: Link,
      genieIcon: 'genie-lamp',
      label: 'Personalizes greeting',
      description: 'Genie references sender, mutual connection, or specific value proposition from outreach'
    },
    {
      type: 'action',
      icon: Bot,
      genieIcon: 'settings',
      label: 'Delivers value proposition',
      description: 'Quickly communicates key benefits relevant to prospect\'s industry or role'
    },
    {
      type: 'action',
      icon: Target,
      genieIcon: 'target',
      label: 'Qualifies interest level',
      description: 'Asks qualifying questions to understand needs, timeline, and decision authority'
    },
    {
      type: 'action',
      icon: Mail,
      genieIcon: 'calendar-check',
      label: 'Captures contact & books meeting',
      description: 'Collects contact info and schedules demo or discovery call if qualified'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Qualified prospect engaged',
      description: 'Higher response rates than traditional cold outreach with immediate qualification'
    }
  ],
  metrics: {
    conversionRate: '12%',
    responseTime: 'Immediate',
    satisfactionScore: '4.3/5',
    costSavings: '$60k/quarter',
    timeToValue: '2 hours'
  },
  testimonials: [
    {
      companyName: 'GrowthHack Solutions',
      authorName: 'Rachel Kim',
      authorTitle: 'Business Development Director',
      quote: 'Genie prospecting links get 4x higher response rates than our traditional cold emails. We\'re booking 40% more qualified meetings.',
      metrics: '4x higher response rates'
    }
  ],
  resources: [
    {
      type: 'template',
      title: 'Cold Outreach Message Templates',
      description: 'Proven email and LinkedIn templates with Genie link integration',
      url: '/templates/cold-outreach-messages'
    },
    {
      type: 'guide',
      title: 'Personalization at Scale',
      description: 'How to customize Genie responses for different industries and roles',
      url: '/docs/outreach-personalization'
    }
  ]
};